import React from 'react';
import { DateTime } from 'luxon';
import styled from 'styled-components';
import { filterBoolean, formatCurrency, formatNumber } from '/imports/utils';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { SolarBill, solarBillAmountDue, solarBillSolarCharges } from '/imports/api/solarBills';

export const NorthshoreBillStyled = styled.div`
  @media screen, print {
    font-family: 'Inter', sans-serif;
    width: 8.5in;
    position: absolute;
    top: 0;
    left: 0;
    .sheet {
      width: 8.5in;
      height: 11in;
      border: 1px solid black;
      box-sizing: border-box;
      overflow: hidden;
      position: relative;
      color: rgb(62, 72, 78);
      div, img, h2 {
        padding: 0;
        margin: 0;
        position: absolute;
      }
    }
  }
`;

/** Printable single-account Northshore solar bill sheet. */
export const NorthshoreBill = (props: { bill: SolarBill }) => {
  const bill = props.bill;
  const [issuedYear, issuedMonth, issuedDay] = bill.issuedDate.split('-');
  const issuedDate = `${issuedMonth}/${issuedDay}/${issuedYear}`;
  const [dueYear, dueMonth, dueDay] = bill.dueDate.split('-');
  const dueDate = `${dueMonth}/${dueDay}/${dueYear}`;
  const [startYear, startMonth, startDay] = bill.startDate.split('-');
  const startDate = `${startMonth}/${startDay}/${startYear}`;
  const [endYear, endMonth, endDay] = bill.endDate.split('-');
  const endDate = `${endMonth}/${endDay}/${endYear}`;
  const daysInPeriod = DateTime.fromISO(bill.endDate).diff(DateTime.fromISO(bill.startDate), 'days').days + 1;
  return (
    <NorthshoreBillStyled className="bill northshore-bill">
      <div className="sheet">
        <div style={{ top: '0', left: '0', width: '100%', height: '4cm' }}>
          <img style={{ top: '0.75cm', left: '0.75cm', height: '2.25cm' }} src="/images/northshore/northshore_logo.png" alt="Northshore" />
          <div style={{ top: '0.5cm', left: '4.25cm', height: '2.5cm', borderRight: '1px solid black' }} />
          <div style={{ top: '1cm', left: '4.75cm', height: '1.8cm', width: '6cm', fontSize: '0.4cm' }}>
            <span>One Town Square, Suite 1550</span><br />
            <span>Southfield, Michigan 48076</span><br />
            <span>solar24llc.com</span>
          </div>
          <div style={{ top: '0.75cm', right: '0.75cm ' }}>Solar electricity powered by</div>
          <img style={{ top: '1cm', right: '0.3cm', height: '1.75cm' }} src="/images/logo_dark.png" />
          <div style={{ top: '3.5cm', left: '0.75cm', right: '0.75cm', width: 'calc(100% - 1.5cm)', borderBottom: '1px solid #9cbfea' }} />
        </div>
        <div style={{ top: '3.5cm', left: '0.75cm' }}>
          <div style={{ top: '0.75cm', width: '5cm', fontWeight: 'bold' }}>DATE:</div><div style={{ top: '0.75cm', left: '4.75cm', width: '5.5cm' }}>{issuedDate}</div>
          <div style={{ top: '1.75cm', width: '5cm', fontWeight: 'bold' }}>ACCOUNT NUMBER:</div><div style={{ top: '1.75cm', left: '4.75cm', width: '5.5cm' }}>{bill.accountNumber}</div>
          <div style={{ top: '2.75cm', width: '5cm', fontWeight: 'bold' }}>ACCOUNT HOLDER:</div><div style={{ top: '2.75cm', left: '4.75cm', width: '5.5cm' }}>{bill.customerName || bill.accountNumber}</div>
          <div style={{ top: '3.75cm', width: '5cm', fontWeight: 'bold' }}>SERVICE ADDRESS:</div><div style={{ top: '3.75cm', left: '4.75cm', width: '5.5cm' }}>{bill.serviceAddress?.formatted || filterBoolean(bill._meters.map((m) => m.address?.formatted)).join(' | ')}</div>
        </div>
        <div style={{ top: '9cm', left: '0.75cm' }}>
          <h2 style={{ top: '0', left: '0', width: '8cm', fontWeight: 'bold', color: '#407aaf' }}>Account Summary</h2>
          <div style={{ top: '1cm', left: '0', background: '#f4f5f6', width: '12.25cm', height: '1.12cm', fontSize: '0.325cm', fontWeight: 'bold' }}>
            <div style={{ top: '1mm', right: '1mm' }}>Current Amount Due</div>
            <div style={{ bottom: '1mm', left: '1mm' }}>Current Solar Charges</div>
            <div style={{ bottom: '1mm', left: '4.15cm' }}>{startDate} - {endDate}</div>
            <div style={{ bottom: '1mm', right: '1mm', width: '3.5cm', textAlign: 'right', paddingRight: '1mm', boxSizing: 'border-box', border: '1px solid black' }}>{formatCurrency(solarBillSolarCharges(bill))}</div>
          </div>
        </div>
        <div style={{ bottom: '1.5cm', left: '0.75cm', width: '12.25cm', fontSize: '0.35cm' }}>
          <p><b>Payment Details. </b>ACH details provided upon request. Customer account will be charged on or around the fifth (5th) day of each month.</p>
          <p><b>Convenience Fee. </b>Customer will be charged $5 per payment for any amount that is not remitted electronically.</p>
          <p><b>Returned Payment Charge. </b>Customer will be charged $25 plus any other transaction fees for checks or other payments that are returned or refused for payment, to the extent such charges are permitted under applicable law.</p>
          <p><b>Late Fee Payment. </b>Customer will be charged a late fee equal to the lesser of (i) $5.00 (five dollars) per month on the portion of your balance that is more than thirty (30) days past due, and (ii) the maximum amount permitted under and subject to applicable law.</p>
        </div>
        <div style={{ top: '4cm', right: '8.25cm', height: '23cm', borderRight: '2px solid #9cbfea' }} />
        <div style={{ top: '4cm', right: '0.75cm', width: '7cm', height: '2.25cm', border: '3px solid #9cbfea' }}></div>
        <div style={{ top: '5.125cm', right: '0.75cm', width: '7.15cm', height: '0', borderBottom: '3px solid #9cbfea' }}></div>
        <div style={{ top: '4.35cm', right: '5.5cm', fontWeight: 'bold' }}>DATE DUE</div>
        <div style={{ top: '4.35cm', right: '1cm', fontWeight: 'bold' }}>{dueDate}</div>
        <div style={{ top: '5.5cm', right: '4.675cm', fontWeight: 'bold' }}>AMOUNT DUE</div>
        <div style={{ top: '5.5cm', right: '1cm', fontWeight: 'bold' }}>{formatCurrency(solarBillAmountDue(bill))}</div>
        <h2 style={{ top: '7cm', right: '2.15cm', color: '#407aaf', fontSize: '0.5cm', fontWeight: 'bold' }}>Solar Production (kWh)</h2>
        <div style={{ top: '7.75cm', right: '0.75cm', width: '7.4cm', height: '5cm' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              margin={{ top: 5, left: -25, right: 5, bottom: 0 }}
              data={bill.historicalProduction}
              barGap={0}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" tickFormatter={(item) => item.toUpperCase()} tick={{ fontSize: 8 }} interval={0} />
              <YAxis tick={{ fontSize: 8 }} />
              <Bar dataKey="production" name="Solar Production" fill="#22B14C" isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div style={{ top: '13cm', right: '0.75cm' }}>
          <table>
            <tr>
              <td style={{ fontWeight: 'bold', color: '#407aaf', textAlign: 'right' }}>{formatNumber(bill.production)} kWh</td>
              <td style={{ fontWeight: 'bold' }}>Solar Production</td>
            </tr>
            <tr>
              <td style={{ fontWeight: 'bold', color: '#407aaf', textAlign: 'right' }}>{daysInPeriod}</td>
              <td style={{ fontWeight: 'bold' }}>Days in Billing Period</td>
            </tr>
          </table>
        </div>
      </div>
    </NorthshoreBillStyled>
  );
};